import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getDetectedReferralCode, clearReferralAttribution } from "@/lib/attribution";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let scriptPromise = null;
function loadGoogleScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export default function GoogleButton({ label = "Continue with Google", testid = "google-signin-btn" }) {
  const { loginWithGoogleCredential } = useAuth();
  const nav = useNavigate();
  const btnRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    async function handleCredential(response) {
      try {
        const referralCode = getDetectedReferralCode() || undefined;
        const user = await loginWithGoogleCredential(response.credential, referralCode);
        clearReferralAttribution();
        toast.success(`Welcome, ${user.name}`);
        nav(user.role === "admin" ? "/admin/dashboard" : "/dashboard");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    }

    loadGoogleScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        });
        if (btnRef.current) {
          window.google.accounts.id.renderButton(btnRef.current, {
            theme: "filled_black",
            size: "large",
            shape: "pill",
            width: btnRef.current.offsetWidth || 320,
            text: label.toLowerCase().includes("sign up") ? "signup_with" : "continue_with",
          });
        }
        setReady(true);
      })
      .catch(() => {
        // Silently fail — button just won't render if Google's script can't load
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="mt-4 w-full flex justify-center" data-testid={testid}>
      <div ref={btnRef} className={ready ? "" : "opacity-0 h-0 overflow-hidden"} />
    </div>
  );
}
